import { useEffect, useRef, useState } from "react";

import type { Progress } from "../../configs/apis/GetProgress/models";
import type { Prize } from "../../configs/apis/models";
import { getShowablePrize } from "../../configs/utils/utility";

export type QueuedReward = {
  challengeId: number;
  prize: Prize;
};

export const useHiddenChallengeRewards = (challenges: Progress[] | undefined) => {
  const [queue, setQueue] = useState<QueuedReward[]>([]);
  const claimedChallengeIds = useRef(new Set<number>());

  useEffect(() => {
    if (!challenges?.length) return;

    setQueue((prevQueue) => {
      const alreadyQueuedIds = new Set(prevQueue.map((reward) => reward.challengeId));

      const newlyDiscovered = challenges.reduce<QueuedReward[]>((acc, challenge) => {
        if (challenge.visible !== false) return acc;
        if (claimedChallengeIds.current.has(challenge.id)) return acc;
        if (alreadyQueuedIds.has(challenge.id)) return acc;

        // Left unrecorded on purpose so it is re-evaluated on every refetch: a Pending
        // prize turns Valid later, and marking it now would drop it for good.
        const prize = getShowablePrize(challenge.prizes);
        if (prize) acc.push({ challengeId: challenge.id, prize });
        return acc;
      }, []);

      return newlyDiscovered.length ? [...prevQueue, ...newlyDiscovered] : prevQueue;
    });
  }, [challenges]);

  const visibleChallenges = challenges?.filter((challenge) => challenge.visible !== false) ?? [];

  const dismissFrontOfQueue = () => setQueue((prev) => prev.slice(1));

  // Only call once the claim mutation succeeds — a hidden challenge has no card to
  // retry from, so marking it early would lose the reward if the claim fails.
  const markChallengeClaimed = (challengeId: number) => {
    claimedChallengeIds.current.add(challengeId);
  };

  return { visibleChallenges, queuedRewards: queue, dismissFrontOfQueue, markChallengeClaimed };
};
